import axios from 'axios';
import * as E from 'fp-ts/lib/Either';
import {flow} from 'fp-ts/lib/function';
import * as t from 'io-ts';
import {useCallback, useEffect, useState} from 'react';
import uuid from 'uuid-random';

import {globalWindow} from '../../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {ErrorWithData} from '../../../../../libs/shared/core/src/helpers/types/customErrors';
import {makeSafeArrayType} from '../../../../../libs/shared/core/src/models/api/types/arrayTypes';
import {makeSafeEnumRuntimeType} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  isAxiosNetworkError,
  isAxiosTimeoutError,
} from '../../../../../libs/shared/core/src/services/http/httpBase';
import {logger} from '../../../../../libs/shared/core/src/services/logger/logger';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {saveUserPreferencesAction} from '../../../../store/data/preferences/preferencesActions';
import {getProductUpdatesSeenAt} from '../../../../store/data/preferences/preferencesSelectors';
import {getUser} from '../../../../store/data/teammates/teammatesSelectors';

/*
 * Constants.
 */

const endpoint = 'https://frontapp.github.io/product-updates/v1.json';

const productionRequestIntervalMs = 1000 * 60 * 2; // Two minutes.
const debugRequestIntervalMs = 1000 * 10; // Ten seconds.
const maxBackoffMultiplier = 32;

export enum ProductUpdateCategory {
  FEATURE = 'feature',
  EVENT = 'event',
  RESOURCE = 'resource',
  TRAINING = 'training',
  ANNOUNCEMENT = 'announcement',
}

/*
 * Types.
 */

const RProductUpdateCategory = makeSafeEnumRuntimeType(
  ProductUpdateCategory,
  ProductUpdateCategory.ANNOUNCEMENT,
);

const RProductUpdate = t.type({
  category: RProductUpdateCategory,
  weight: t.number,
  timestamp: t.number,
  title: t.string,
  linkUrl: t.string,
  body: t.string,
});

const RProductUpdates = makeSafeArrayType(RProductUpdate);

export interface ProductUpdate extends t.TypeOf<typeof RProductUpdate> {
  id: string; // We're stubbing ID until the BE implements it.
}

/*
 * Helpers.
 */

export function useFetchProductUpdatesPeriodically() {
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const productUpdatesSeenAt = useAppSelector(getProductUpdatesSeenAt);
  const [productUpdates, setProductUpdates] = useState<ReadonlyArray<ProductUpdate> | null>(null);

  const productUpdateTimestamps = (productUpdates ?? []).map((productUpdate) => productUpdate.timestamp);
  const maxProductUpdateTimestamp =
    productUpdateTimestamps.length > 0 ? Math.max(...productUpdateTimestamps) : undefined;
  const pendingProductUpdatesSeenAt =
    (productUpdatesSeenAt ?? 0) < (maxProductUpdateTimestamp ?? 0) ? maxProductUpdateTimestamp : undefined;

  const onProductUpdatesSeen = useCallback(() => {
    if (pendingProductUpdatesSeenAt === undefined) {
      return;
    }

    dispatch(saveUserPreferencesAction({productUpdatesSeenAt: pendingProductUpdatesSeenAt}));
  }, [dispatch, pendingProductUpdatesSeenAt]);

  useEffect(() => {
    const requestIntervalMs =
      globalWindow.location?.hash === '#debug-in-app-messaging'
        ? debugRequestIntervalMs
        : productionRequestIntervalMs;

    const abortController = new AbortController();
    fetchProductUpdatesPeriodically(
      (fetchedProductUpdates) => setProductUpdates(fetchedProductUpdates),
      requestIntervalMs,
      abortController.signal,
    );

    return () => {
      abortController.abort();
    };
  }, []);

  const productUpdatesSeen = computeProductUpdatesSeen(user.createdAt, productUpdatesSeenAt, productUpdates);

  return {
    productUpdates,
    hasUnseenProductUpdates: !productUpdatesSeen,
    onProductUpdatesSeen,
  };
}

function computeProductUpdatesSeen(
  userCreatedAt: number,
  productUpdatesSeenAt: number | null,
  productUpdates: ReadonlyArray<ProductUpdate> | null,
) {
  return (productUpdates ?? []).every((productUpdate) =>
    productUpdatesSeenAt === null
      ? userCreatedAt >= productUpdate.timestamp
      : productUpdatesSeenAt >= productUpdate.timestamp,
  );
}

const decodeProductUpdates = flow(
  RProductUpdates.decode,
  E.map((productUpdates) => productUpdates.map((productUpdate) => ({id: uuid(), ...productUpdate}))),
  E.getOrElse((errors): ReadonlyArray<ProductUpdate> => {
    throw new ErrorWithData('Invalid product updates response', {errors});
  }),
);

async function fetchProductUpdatesAsync(signal: AbortSignal): Promise<ReadonlyArray<ProductUpdate>> {
  const response = await axios.get(endpoint, {signal});
  return decodeProductUpdates(response.data);
}

function fetchProductUpdatesPeriodically(
  onFetch: (productUpdates: ReadonlyArray<ProductUpdate>) => void,
  interval: number,
  signal: AbortSignal,
) {
  let backoffErrorCount = 0;
  let minimumDelay = 0;

  const performFetchAsync = async () => {
    try {
      const productUpdates = await fetchProductUpdatesAsync(signal);

      backoffErrorCount = 0;
      minimumDelay = 0;

      onFetch(productUpdates);
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response !== undefined &&
        (error.response.status === 429 || error.response.status >= 500)
      ) {
        backoffErrorCount += 1;

        // Respect Retry-After header.
        const retryAfterDelay = Number(error.response.headers['retry-after']);
        minimumDelay = isFinite(retryAfterDelay) ? retryAfterDelay * 1000 : 0;
      }

      logProductUpdatesFetchError(error);
    } finally {
      // Add exponential backoff.
      const delay = interval * Math.min(2 ** backoffErrorCount, maxBackoffMultiplier);

      setTimeoutWithSignal(performFetchAsync, Math.max(minimumDelay, delay), signal);
    }
  };

  performFetchAsync();
}

function logProductUpdatesFetchError(error: any) {
  const isIgnorableError =
    error.name === 'AbortError' ||
    axios.isCancel(error) ||
    (axios.isAxiosError(error) && (isAxiosNetworkError(error) || isAxiosTimeoutError(error)));

  if (!isIgnorableError) {
    logger.warn('Failed to fetch product updates', {
      error: String(error),
    });
  }
}

// TODO: Standardize.
function setTimeoutWithSignal(onTimeout: () => void, timeout: number, signal: AbortSignal) {
  // This check is recommended, and we've seen Node.js crash if we add an event listener to an aborted signal.
  // https://nodejs.org/docs/latest-v20.x/api/globals.html#event-abort
  if (signal.aborted) {
    return;
  }

  let handle: number;

  const onAbort = () => {
    globalWindow.clearTimeout(handle);
  };

  signal.addEventListener('abort', onAbort, {once: true});
  handle = globalWindow.setInstrumentedTimeout(() => {
    signal.removeEventListener('abort', onAbort);
    onTimeout();
  }, timeout);
}
